import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import { getNodeTestIdProp } from '@/utils';
import { ProductsPageView } from './ProductsPageView';
import { useProductsPage } from './useProductsPage';

/**
 * /products page — the product-management view. Owns its data hook
 * (`useProductsPage`) and an `ErrorBoundary`; the throw lives in the inner
 * Content child because React boundaries don't catch errors thrown by the
 * component that renders them. Create / edit / detail are separate routes, so
 * there are no page-level modals here.
 */
export const ProductsPage: React.FC = () => {
    const page = useProductsPage();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('products.components.productTable.errorTitle'),
            errorMessage: t('products.components.productTable.errorMessage'),
            errorRetry: t('products.components.productTable.errorRetry'),
        }),
        [t],
    );

    return (
        <div
            className="mx-auto flex w-full max-w-7xl flex-col gap-8"
            {...getNodeTestIdProp('products-page')}
        >
            <ErrorBoundary
                data-testid="products-page-error-boundary"
                fallbackMessage={copy.errorMessage}
                id="products-page-error-boundary"
                onRetry={page.onRetry}
                resetKeys={[page.error]}
                retryLabel={copy.errorRetry}
                title={copy.errorTitle}
            >
                <ProductsPageContent page={page} />
            </ErrorBoundary>
        </div>
    );
};

type ProductsPageContentProps = {
    page: ReturnType<typeof useProductsPage>;
};

const ProductsPageContent: React.FC<ProductsPageContentProps> = ({ page }) => {
    // The catalog's failure is the catalog tab's: Barcode issues reads and
    // fails on its own (review of #241, finding 10).
    if (page.error && page.view === 'catalog') {
        throw page.error;
    }

    return (
        <ProductsPageView
            appliedFilterTags={page.appliedFilterTags}
            barcodeIssuesCount={page.barcodeIssuesCount}
            branchId={page.branchId}
            branchScope={page.branchScope}
            brandOptions={page.brandOptions}
            catalogCount={page.catalogCount}
            categoryOptions={page.categoryOptions}
            data={page.data}
            exportScope={page.exportScope}
            filterCount={page.filterCount}
            filtersDrawer={page.filtersDrawer}
            hasActiveFilters={page.hasActiveFilters}
            isDraftsOnly={page.isDraftsOnly}
            isBranchPending={page.isBranchPending}
            isFetching={page.isFetching}
            onCreate={page.onCreate}
            onPageChange={page.onPageChange}
            onPageSizeChange={page.onPageSizeChange}
            onRemoveFilter={page.onRemoveFilter}
            onResetFilters={page.onResetFilters}
            onSearchChange={page.onSearchChange}
            onShowDrafts={page.onShowDrafts}
            onViewChange={page.onViewChange}
            page={page.page}
            pageSize={page.pageSize}
            pageWindow={page.pageWindow}
            productLinkState={page.productLinkState}
            search={page.search}
            stockFigures={page.stockFigures}
            totalCount={page.totalCount}
            totalPages={page.totalPages}
            view={page.view}
        />
    );
};
